import React, { useState, useEffect } from 'react';
import { Card, Spinner, Alert, Badge, Button, Modal, Row, Col } from 'react-bootstrap';
import { FaTrashAlt, FaComment, FaPlus } from "react-icons/fa";
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext";
import { chatAPI } from '../../api/apiClient';
import PaginationComponent from '../common/PaginationComponent';
import { useTranslation } from 'react-i18next';
import Swal from "sweetalert2";
import '../../assets/css/Chat.css';

export default function DriverList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isSuperAdmin, checkSuperAdminStatus } = useAuth();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteName, setDeleteName] = useState('');
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  // Fetch conversations with pagination and filter by User role
  const fetchConversations = async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const response = await chatAPI.conversations({
        page,
        per_page: pagination.per_page,
      });

      if (response.success) {
        const filteredConversations = response.data.data.filter(
          (conv) => conv.conversation_with.role_name === 'Driver'
        );
        setConversations(filteredConversations);
        setPagination({
          current_page: response.data.meta.current_page,
          last_page: response.data.meta.last_page,
          per_page: response.data.meta.per_page,
          total: response.data.meta.total,
        });
      } else {
        setError(response.message || t('failed_to_fetch_conversations'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_conversations'));
      console.error('Error fetching conversations:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    checkSuperAdminStatus();
  }, []);

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return `${t('yesterday')} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleString([], {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await chatAPI.delete(deleteId);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("success"),
          text: t("conversation_deleted_successfully"),
          timer: 2000,
          showConfirmButton: false,
        });
        setShowDelete(false);
        fetchConversations(pagination.current_page);
      } else {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: response.message || t("failed_to_delete_conversation"),
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t("failed_to_delete_conversation"),
        timer: 2000,
        showConfirmButton: false,
      });
      console.error("Error deleting conversation:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (e, conversation, id) => {
    e.stopPropagation();
    setDeleteId(id);
    setDeleteName(conversation.conversation_with.name);
    setShowDelete(true);
  };

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <Spinner animation="border" />
        <p className="mt-2">{t('loading')}</p>
      </div>
    );
  }

  return (
    <>
      <div className="container py-4">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-4">
          <h4 className="fw-bold mb-0">💬 {t('Conversations')}</h4>
          <Button 
            variant="success" 
            onClick={() => navigate('/admin/driver-list/select-driver')}
            className="d-flex align-items-center rounded-pill px-4"
          >
            <FaPlus className="me-2" />
            {t('new_message')}
          </Button>
        </div>
        
        {error && <Alert variant="danger">{error}</Alert>}
        
        {conversations.length === 0 ? (
          <Alert variant="info">{t('no_conversations')}</Alert>
        ) : (
          <Row>
            {conversations.map(conversation => (
              <Col 
                key={conversation.conversation_with.id} 
                xs={12} 
                sm={6} 
                md={6} 
                lg={4} 
                xl={3}
                className="mb-4"
              >
                <Card 
                  className="h-100 shadow-sm conversation-card"
                  onClick={() => navigate(`/admin/driver-list/chat-driver/${conversation.conversation_with.id}`, {
                    state: { conversationId: conversation.latest_message?.id }
                  })}
                >
                  <Card.Body className="d-flex flex-column p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center flex-grow-1 me-2">
                        <div className="avatar-circle me-2 flex-shrink-0">
                          {conversation.conversation_with.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-grow-1 overflow-hidden">
                          <h6 className="fw-bold mb-0 text-color text-truncate">
                            {conversation.conversation_with.name}
                          </h6>
                          <p className="mb-0 text-muted small text-truncate">
                            {conversation.conversation_with.email || t('not_provided')}
                          </p>
                        </div>
                      </div>
                      {conversation.unread_count > 0 && (
                        <Badge bg="danger" pill className="flex-shrink-0">
                          {conversation.unread_count}
                        </Badge>
                      )}
                    </div>

                    {conversation.latest_message && (
                      <div className="bg-light p-2 rounded mb-3 flex-grow-1">
                        <p className="mb-1 text-dark small text-truncate-2-lines" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}>
                          {conversation.latest_message.message}
                        </p>
                        <small className="text-muted">
                          {conversation.last_message_time}
                        </small>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/driver-list/chat-driver/${conversation.conversation_with.id}`, {
                            state: { conversationId: conversation.latest_message?.id }
                          });
                        }}
                        className="d-flex align-items-center rounded-pill px-3 flex-grow-1 me-2"
                        style={{maxWidth: 'fit-content'}}
                      >
                        <FaComment className="me-1" />
                        {t('chat')}
                      </Button>

                      {isSuperAdmin && (
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={(e) => handleDeleteClick(e, conversation, conversation.conversation_with.id)}
                          className="rounded-circle flex-shrink-0"
                          style={{width: '32px', height: '32px'}}
                        >
                          <FaTrashAlt />
                        </Button>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
        
        {pagination.last_page > 1 && (
          <div className="mt-4">
            <PaginationComponent
              currentPage={pagination.current_page}
              lastPage={pagination.last_page}
              onPageChange={fetchConversations}
            />
          </div>
        )}
      </div>

      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("confirm_deletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("confirm_delete_conversation")} <strong>{deleteName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={loading}>
            {t("cancel")}
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={loading}>
            {loading ? t("deleting") : t("delete")}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .conversation-card {
          border: none;
          border-radius: 14px;
          background: linear-gradient(145deg, #ffffff, #f8f9fa);
          transition: all 0.3s ease;
          cursor: pointer;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 220px;
          overflow: hidden;
          word-wrap: break-word;
        }

        .conversation-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 6px 12px rgba(0,0,0,0.15);
          background: linear-gradient(145deg, #f8f9fa, #ffffff);
        }

        .avatar-circle {
          width: 45px;
          height: 45px;
          background: #007bff;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 18px;
          flex-shrink: 0;
        }

        .text-color {
          color: #2c3e50;
        }

        .conversation-card .card-body {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 1rem;
          overflow: hidden;
        }

        .text-truncate-2-lines {
          display: -webkit-box !important;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .progress-label {
          font-weight: 500;
          color: #1b1f3b;
        }

        /* Buttons */
        .conversation-card .btn-primary {
          background-color: #007bff;
          border: none;
          white-space: nowrap;
        }

        .conversation-card .btn-primary:hover {
          background-color: #0056b3;
        }

        .conversation-card .btn-outline-danger {
          border-color: #dc3545;
          color: #dc3545;
        }

        .conversation-card .btn-outline-danger:hover {
          background-color: #dc3545;
          color: white;
        }

        /* Fix overflowing content */
        .conversation-card h6,
        .conversation-card p {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* RESPONSIVE FIXES */
        @media (max-width: 992px) {
          .avatar-circle {
            width: 40px;
            height: 40px;
            font-size: 16px;
          }
          .conversation-card {
            min-height: 200px;
          }
        }

        @media (max-width: 768px) {
          .conversation-card {
            padding: 0.5rem;
            min-height: auto;
          }
          .conversation-card h6 {
            font-size: 0.95rem;
          }
          .conversation-card p {
            font-size: 0.85rem;
          }
          .conversation-card .btn {
            padding: 0.3rem 0.6rem;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 576px) {
          .conversation-card {
            padding: 0.75rem;
          }
          .avatar-circle {
            width: 34px;
            height: 34px;
            font-size: 14px;
          }
          .conversation-card h6 {
            font-size: 0.9rem;
          }
          .conversation-card p {
            font-size: 0.8rem;
          }
          .conversation-card .btn {
            font-size: 0.8rem;
          }
        }
      `}</style>
    </>
  );
}